import { IAppContext } from 'App/AppContext';
import { newGuidNil } from 'utils/guid';
import GlobalDataCache from './globalDataCache';

//
// A list of reference data the app keeps for the session, filled from the login answer, with a lookup by id.
// A lookup of an id that is not there counts a miss and returns an empty item, so a render never breaks on
// data that changed on the server in the meantime.
//
export class EntityCache<T> {
  private _items: T[];

  private _itemSet: Map<number | string, T>;

  misses: number;

  appContext?: IAppContext;

  cacheRef?: GlobalDataCache;

  getId: (item: T) => number | string;

  getEmptyItem: (id: string | number | undefined) => T;

  constructor(getId: (item: T) => number | string, getEmptyItem: (id: string | number | undefined) => T) {
    this._itemSet = new Map<number | string, T>();
    this._items = [];
    this.misses = 0;
    this.getId = getId;
    this.getEmptyItem = getEmptyItem;
  }

  private createItemSet() {
    this._itemSet.clear();
    this._items.forEach((i) => {
      this._itemSet.set(this.getId(i), i);
    });
  }

  get items() {
    return this._items;
  }

  set items(val: T[]) {
    this._items = val;
    this.createItemSet();
  }

  has(id: number | string | undefined): boolean {
    return id !== undefined && this._itemSet.has(id);
  }

  get(id: number | string | undefined): T {
    if (id === undefined || id === 0 || id === newGuidNil()) {
      return this.getEmptyItem(id);
    }

    const item = this._itemSet.get(id);
    if (item) {
      return item;
    }

    this.misses++;
    if (this.appContext) {
      this.appContext.cacheMiss(this.misses);
    }

    return this.getEmptyItem(id);
  }

  getItemsForId(ids: (number | string)[] | undefined): T[] {
    if (!ids) return [];

    return ids.reduce<T[]>((result, id) => {
      const item = this._itemSet.get(id);
      if (item) result.push(item);

      return result;
    }, []);
  }

  add(item: T) {
    if (!this._itemSet.has(this.getId(item))) {
      this._items.push(item);
      this._itemSet.set(this.getId(item), item);
    }
  }

  update(item: T) {
    const idx = this._items.findIndex((i) => this.getId(i) === this.getId(item));
    if (idx >= 0) {
      this._items[idx] = item;
      this._itemSet.set(this.getId(item), item);
    }
  }

  remove(item: T) {
    const idx = this._items.findIndex((i) => this.getId(i) === this.getId(item));
    if (idx >= 0) {
      this._items.splice(idx, 1);
      this._itemSet.delete(this.getId(item));
    }
  }
}
